/**
 * MenuBar User — avatar + dropdown (Profil, Eski Dashboard, Ayarlar, Cikis)
 */
import { AlertCircle, ChevronDown, LayoutDashboard, LogOut, Settings, Shield, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import { useOSTheme } from '../OSThemeContext'

export default function MenuBarUser() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { toggle, theme } = useOSTheme()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false) }
    }
    if (open) { document.addEventListener('mousedown', handler) }
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!user) { return null }

  const initial = (user.display_name ?? user.username ?? 'U').charAt(0).toUpperCase()

  const menuItem = (icon: React.ReactNode, label: string, onClick: () => void, color?: string) => (
    <button
      onClick={() => { onClick(); setOpen(false) }}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        width: '100%', padding: '8px 12px',
        background: 'transparent', border: 'none',
        color: color ?? 'var(--os-text)', fontSize: '12px',
        cursor: 'pointer', textAlign: 'left', borderRadius: '6px',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-hover)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
    >
      {icon}
      {label}
    </button>
  )

  return (
    <div ref={ref} style={{ position: 'relative', marginLeft: '8px' }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', gap: '5px',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--os-muted)', padding: '2px 6px', borderRadius: '6px',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-hover)' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
      >
        {/* Avatar */}
        <div style={{
          width: '22px', height: '22px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #818cf8, #c084fc)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '10px', fontWeight: 800, color: '#fff',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          border: '1px solid rgba(255,255,255,0.2)'
        }}>
          {initial}
        </div>
        <span style={{ 
          fontSize: '12px', 
          fontWeight: 500,
          maxWidth: '100px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          color: 'var(--os-text)'
        }}>
          {user.display_name ?? user.username}
        </span>
        <ChevronDown size={12} style={{ opacity: 0.6 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '28px', right: 0,
          background: 'var(--os-win-bg)',
          backdropFilter: 'blur(20px)',
          border: '1px solid var(--os-border)',
          borderRadius: '10px',
          padding: '6px',
          minWidth: '180px',
          zIndex: 9999,
          boxShadow: 'var(--os-win-shadow)',
          animation: 'winIn .2s ease-out',
        }}>
          {/* User info */}
          <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid var(--os-border)', marginBottom: '4px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--os-text)' }}>{user.display_name}</div>
            <div style={{ fontSize: '10px', color: 'var(--os-muted)' }}>@{user.username}</div>
          </div>

          {menuItem(<User size={13} />, 'Profile', () => navigate(`/profile/${user.username ?? ''}`))}
          {menuItem(<LayoutDashboard size={13} />, 'RunAI OS Dashboard', () => navigate('/dashboard'))}
          {menuItem(<LayoutDashboard size={13} />, 'Classic Dashboard', () => navigate('/aicloud/app/dashboard'))}
          {menuItem(<Settings size={13} />, 'System Settings', () => navigate('/dashboard/settings'))}
          {(user.role === 'admin' || user.role === 'both') && menuItem(<Shield size={13} />, 'Admin Panel', () => navigate('/admin'), '#f59e0b')}
          {menuItem(<AlertCircle size={13} />, 'Help & Support', () => navigate('/support'))}

          {/* Theme toggle */}
          <button
            onClick={() => { toggle(); setOpen(false) }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              width: '100%', padding: '8px 12px',
              background: 'transparent', border: 'none',
              color: 'var(--os-text)', fontSize: '12px',
              cursor: 'pointer', textAlign: 'left', borderRadius: '6px',
              transition: 'background 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--os-hover)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
          >
            <span style={{ fontSize: '12px' }}>{theme === 'dark' ? '☀️' : '🌙'}</span>
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>

          <div style={{ borderTop: '1px solid var(--os-border)', margin: '4px 0' }} />
          {menuItem(<LogOut size={13} />, 'Sign Out', () => { logout(); navigate('/login') }, '#ef4444')}
        </div>
      )}
    </div>
  )
}
